import React, { createContext, useState, useEffect, useContext } from 'react';
import { AuthService } from '../api/AuthService';
import { useLocation } from 'react-router-dom';
import { RBContext } from './RBContext';
import { RoutePathConstant, roleNameEnum } from '../util/Common';

// Todo: define proper types.
export interface AccountInfoContextType {
  accountInformation: any;
  setAccountInformation: React.Dispatch<React.SetStateAction<any>>;
  isPageLoading: boolean;
  fetchAccountInfo: () => Promise<any | null>;
  fetchPharmacy: (pharmacyId: any, options?: { preferCache?: boolean }) => Promise<any>;
}

export const AccountInfoContext = createContext<AccountInfoContextType>({
  accountInformation: null,
  setAccountInformation: () => {},
  isPageLoading: true,
  fetchAccountInfo: async () => null,
  fetchPharmacy: async () => null,
});

const regionalManagerThemeRoutes = [
  RoutePathConstant.RegionalManagerDashboard,
].map((path) => path.replace(/\/+$/, ""));

export const AccountInfoProvider = ({ ...props}) => {

  // TODO: This should REALLY be typed properly.
  const [accountInformation, setAccountInformation] = useState<any>(null);
  const [isPageLoading, setIsPageLoading] = useState<any>(true);
  const location = useLocation();
  const {
    updateprimaryColor,
    updateSecondaryColor,
    updateContactEmail,
    updateContactPhone,
    updateMode,
  } = useContext(RBContext);

  // Apply a complete theme payload into both localStorage and RB context.
  const applyStoredTheme = ({
    primaryColor,
    secondaryColor,
    contactEmail,
    contactPhone,
    logo,
    industryMode,
  }: any) => {
    localStorage.setItem("primaryColor", primaryColor || "");
    localStorage.setItem("secondaryColor", secondaryColor || "");
    localStorage.setItem("contactEmail", contactEmail || "");
    localStorage.setItem("contactPhone", contactPhone || "");
    localStorage.setItem("logo", logo || "");
    localStorage.setItem("industryMode", industryMode || "");

    updateprimaryColor(primaryColor || "");
    updateSecondaryColor(secondaryColor || "");
    updateContactEmail(contactEmail || "");
    updateContactPhone(contactPhone || "");
    updateMode(industryMode || "");
  };

  // Route-aware theme selection:
  // RM dashboard uses RM network theme, other RM pages use emulated owner theme,
  // and owner/worker flows keep their existing behavior.
  const applyThemeForCurrentContext = (userData: any) => {
    const isRegionalManager = AuthService.getRoleName() === roleNameEnum.regionalmanager;
    const normalizedPath = location.pathname.replace(/\/+$/, "");
    const shouldUseRegionalManagerTheme =
      isRegionalManager && regionalManagerThemeRoutes.includes(normalizedPath);
    const activePharmacy = userData?.pharmacies?.find(
      (pharmacy: any) => pharmacy.id === userData?.owner?.defaultPharmacyId
    );

    // Force the RM network theme on explicitly listed RM-only pages.
    if (shouldUseRegionalManagerTheme) {
      applyStoredTheme({
        primaryColor: localStorage.getItem("rmPrimaryColor"),
        secondaryColor: localStorage.getItem("rmSecondaryColor"),
        contactEmail: localStorage.getItem("rmContactEmail"),
        contactPhone: localStorage.getItem("rmContactPhone"),
        logo: localStorage.getItem("rmLogo"),
        industryMode: localStorage.getItem("rmIndustryMode"),
      });
      return;
    }

    // For normal RM pages, use the currently emulated owner's network theme.
    if (isRegionalManager && userData?.networks?.[0]) {
      applyStoredTheme({
        primaryColor: userData.networks[0].brand?.primaryColor,
        secondaryColor: userData.networks[0].brand?.secondaryColor,
        contactEmail: userData.networks[0].contactEmail,
        contactPhone: userData.networks[0].contactPhone,
        logo: userData.networks[0].brand?.emailLogo,
        industryMode: activePharmacy?.industryMode || localStorage.getItem("industryMode"),
      });
      return;
    }

    // For non-RM accounts, keep brand contact/colors in RB context.
    if (userData?.networks?.[0]) {
      updateprimaryColor(userData.networks[0].brand?.primaryColor);
      updateSecondaryColor(userData.networks[0].brand?.secondaryColor);
      updateContactEmail(userData.networks[0].contactEmail);
      updateContactPhone(userData.networks[0].contactPhone);
    }

    // Owners derive industry mode from their active default pharmacy.
    if (userData?.owner?.defaultPharmacyId && Array.isArray(userData?.pharmacies)) {
      updateMode(activePharmacy?.industryMode || localStorage.getItem("industryMode"));
      return;
    }

    // Workers keep using their stored industry mode fallback.
    if (String(userData?.roleName || "").toLowerCase() === roleNameEnum.pharmacist) {
      updateMode(localStorage.getItem("industryMode"));
      return;
    }

    // Final fallback for any remaining account shapes.
    updateMode(localStorage.getItem("industryMode"));
  };

  // Helper Function to fetch account info and update the context state
  const fetchAccountInfo: () => Promise<any | null> = async () => {
    setIsPageLoading(true);
    try {

      // Fetch the account info
      const userData = await AuthService.loadAccountInfo();

      // If the user is an owner, store their default pharmacy name in localStorage for display in the side menu.
      if (userData && AuthService.getRoleName() === roleNameEnum.owner) {
        const defaultLocationName =
          userData?.pharmacies?.find(
            (pharmacy: any) => pharmacy.id === userData?.owner?.defaultPharmacyId
          )?.name;

        if (defaultLocationName) {
          localStorage.setItem("pharmacyName", defaultLocationName);
        }
      }

      applyThemeForCurrentContext(userData);
      setAccountInformation(userData);
      return userData ?? null;

    } catch (error) {
      // Keep the previously loaded account information rather than wiping it on a transient failure.
      console.error('Error fetching account info:', error);
      return null;

    } finally {
      setIsPageLoading(false);
    }
  };

  // Wrapper around AuthService.fetchPharmacy that can serve the pharmacy straight from
  // the already-loaded account, avoiding a redundant request.
  //
  // Pass { preferCache: true } ONLY when the lightweight fields the account payload
  // carries are enough (id, name, networkId, paymentType, jobPostFlag). For full
  // pharmacy detail (creditCards, surcharge config, disabledPaymentMethods, ...) leave
  // it off so it fetches the complete record from the pharmacy/:id endpoint.
  const fetchPharmacy = async (
    pharmacyId: any,
    { preferCache = false }: { preferCache?: boolean } = {}
  ) => {

    console.log("fetchPharmacy called with pharmacyId:", pharmacyId, "preferCache:", preferCache);
    if (preferCache) {
      const cachedPharmacy = accountInformation?.pharmacies?.find(
        (pharmacy: any) => String(pharmacy?.id) === String(pharmacyId)
      );
      if (cachedPharmacy) {
        console.log(`Serving pharmacy ${pharmacyId} from account context cache`);
        return cachedPharmacy;
      }
    }
    return AuthService.fetchPharmacy(pharmacyId);
  };


  // On Context Mount, Run the following.
  useEffect(() => {

    fetchAccountInfo();

    const handleRegionalManagerLocationChange = async () => {
      await fetchAccountInfo();
    };

    window.addEventListener(
      "regional-manager-location-changed",
      handleRegionalManagerLocationChange
    );

    return () => {
      window.removeEventListener(
        "regional-manager-location-changed",
        handleRegionalManagerLocationChange
      );
    };
    
  }, []);

  useEffect(() => {
    if (accountInformation && Object.keys(accountInformation).length > 0) {
      applyThemeForCurrentContext(accountInformation);
    }
  }, [location.pathname, accountInformation]);

  return (
    <AccountInfoContext.Provider value={
        {
          accountInformation,
          setAccountInformation,
          isPageLoading,
          fetchAccountInfo,
          fetchPharmacy,
        } as AccountInfoContextType
    }>
      {props.children}
    </AccountInfoContext.Provider>
  );
};

// export const useAccountInfo = () => {
//   return useContext(AccountInfoContext);
// };
